import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import { ImageProvider, useImages } from './context/ImageContext';
import { ProductsProvider } from './context/ProductsContext';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import MedicalAlertsPage from './pages/MedicalAlertsPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import OurStoryPage from './pages/OurStoryPage';
import HelpPage from './pages/HelpPage';
import MyAccountPage from './pages/MyAccountPage';
import BlogPostPage from './pages/BlogPostPage';
import HealthcareProvidersPage from './pages/HealthcareProvidersPage';
import CurrentMembersPage from './pages/CurrentMembersPage';
import ReferFriendPage from './pages/ReferFriendPage';
import AccessoriesPage from './pages/AccessoriesPage';
import WhereItWorksPage from './pages/WhereItWorksPage';
import ReviewsPage from './pages/ReviewsPage';
import BlogFallPreventionPage from './pages/BlogFallPreventionPage';
import BlogHomeSafetyPage from './pages/BlogHomeSafetyPage';
import BlogCaregiverPaymentPage from './pages/BlogCaregiverPaymentPage';
import BlogListingPage from './pages/BlogListingPage';
import BlogAdminPage from './pages/BlogAdminPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ComparePage from './pages/ComparePage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import RequestBrochurePage from './pages/RequestBrochurePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SignUpPage from './pages/SignUpPage';
import RequestAdminAccess from './pages/RequestAdminAccess';
import DecryptOrdersReportPage from './pages/DecryptOrdersReportPage';
import TroubleshootingPage from './pages/TroubleshootingPage';
import CampaignsPage from './pages/CampaignsPage';
import TemplateDesignerPage from './pages/TemplateDesignerPage';
import ContactsPage from './pages/ContactsPage';
import MessagingPage from './pages/MessagingPage';
import ShipmentsPage from './pages/ShipmentsPage';
import ExcelToolsPageMinimal from './pages/ExcelToolsPageMinimal';
import RecordMatchingPage from './pages/RecordMatchingPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AccessoryDetailPage from './pages/AccessoryDetailPage';
import FirstAlertMedicalSystemPage from './pages/FirstAlertMedicalSystemPage';
import MedicareMedicaidPage from './pages/MedicareMedicaidPage';
import CompareMedicalAlertSystemsPage from './pages/CompareMedicalAlertSystemsPage';
import CompareAlert1ToLifeAlertPage from './pages/CompareAlert1ToLifeAlertPage';
import CompareAlert1ToBayAlarmPage from './pages/CompareAlert1ToBayAlarmPage';
import CompareAlert1ToMedicalGuardianPage from './pages/CompareAlert1ToMedicalGuardianPage';
import CompareAlert1ToGreatCallPage from './pages/CompareAlert1ToGreatCallPage';
import CompareAlert1ToMobileHelpPage from './pages/CompareAlert1ToMobileHelpPage';
import CompareAlert1ToAdtHealthPage from './pages/CompareAlert1ToAdtHealthPage';
import PersonalEmergencyResponseSystemPage from './pages/PersonalEmergencyResponseSystemPage';
import LifeAlertComparedToAlert1Page from './pages/LifeAlertComparedToAlert1Page';
import LifeAlertAlternativesPage from './pages/LifeAlertAlternativesPage';
import SeniorCellPhoneReviewsPage from './pages/SeniorCellPhoneReviewsPage';
import MedicalAlertBraceletsPage from './pages/MedicalAlertBraceletsPage';
import MedicalAlertGlossaryPage from './pages/MedicalAlertGlossaryPage';
import BabyBoomerFactsPage from './pages/BabyBoomerFactsPage';
import HealthCheckPage from './pages/HealthCheckPage';
import LandingPageTest from './pages/LandingPageTest';
import NotFoundPage from './pages/NotFoundPage';
import { pushPageView } from './utils/gtm';
import { OpenGraphHead } from './components/OpenGraphHead';
import { OG_META } from './config/ogMeta';
import { APP_STATIC_ROUTE_SET, NOINDEX_PATHS } from './config/publicRouting';

function normalizePathname(pathname: string): string {
  const normalized = `/${pathname}`.replace(/\/+/g, '/').replace(/\/+$/, '');
  return normalized || '/';
}

function isRecognizedRoute(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname);
  return APP_STATIC_ROUTE_SET.has(normalizedPathname);
}

const NORMALIZED_NOINDEX_PATHS = new Set(NOINDEX_PATHS.map(normalizePathname));

// Navigation wrapper component that hides nav on checkout (order-success shows header)
function NavigationWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isCheckoutPage = location.pathname === '/checkout';
  
  if (isCheckoutPage) {
    return null; // Don't show navigation on checkout
  }
  
  return <>{children}</>;
}

// Scroll to top component with hash support
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    pushPageView(pathname);
  }, [pathname]);

  useEffect(() => {
    if (hash) {
      // If there's a hash, scroll to that element with offset
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const yOffset = -100; // Offset to show content above (like navbar height + padding)
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 0);
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

function AppContent() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMedicalAlertsOpen, setMobileMedicalAlertsOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  
  // Get images from context
  const { getImage } = useImages();
  
  // Get specific images from the context
  const logoImage = getImage('logo.webp');
  const dropdownImage1 = getImage('ez-care-in-home-medical-alert-1.jpg');
  const dropdownImage2 = getImage('ez-care-plus-in-home-medical-alert-1.jpg');
  const dropdownImage3 = getImage('lifebeacon-on-the-go-1.jpg');

  const handleDropdownClose = () => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
    setMobileMedicalAlertsOpen(false);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileMedicalAlertsOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const pathname = normalizePathname(location.pathname);
  const ogMeta = OG_META[pathname];
  const defaultMeta = OG_META['/'];
  const meta = ogMeta || defaultMeta;
  const noIndex = NORMALIZED_NOINDEX_PATHS.has(pathname) || !isRecognizedRoute(pathname);

  return (
    <>
      <ScrollToTop />
      <OpenGraphHead
        title={meta.title}
        description={meta.description}
        pathOverride={pathname + (location.search || '')}
        noIndex={noIndex}
      />
      <div className="App">
        <NavigationWrapper>
            <nav className="navbar" ref={navRef}>
              {/* Top Row: Logo and Phone */}
              <div className="nav-top-row">
                <div className="nav-container">
                  {/* Hamburger - mobile only */}
                  <button
                    type="button"
                    className="nav-hamburger"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={mobileMenuOpen}
                  >
                    {mobileMenuOpen ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <line x1="3" y1="12" x2="21" y2="12"/>
                        <line x1="3" y1="18" x2="21" y2="18"/>
                      </svg>
                    )}
                  </button>
                  <Link to="/" className="nav-logo" onClick={handleDropdownClose}>
                    {logoImage && (
                      <img 
                        src={logoImage} 
                        alt="Alert1 Logo" 
                        className="logo-icon uploaded-logo" 
                        width="120" 
                        height="auto"
                      />
                    )}
                  </Link>
                  
                  {/* Phone Number Next to Logo */}
                  <div className="nav-phone-large">
                    <span className="phone-label">Call Today: </span>
                    <a href="tel:1-877-579-9862" className="phone-number-large">1-877-579-9862</a>
                  </div>
                  
                  {/* Right Side: Help, My Account and Cart */}
                  <div className="nav-right-items">
                    <Link to="/faq" className="nav-help-link">Help</Link>
                    <Link to="/my-account" className="nav-account-link">My Account</Link>
                    <Link to="/cart" className="nav-cart-link">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="9" cy="21" r="1"/>
                        <circle cx="20" cy="21" r="1"/>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Bottom Row: Navigation Links - collapsible on mobile */}
              <div className={`nav-bottom-row ${mobileMenuOpen ? 'nav-mobile-open' : ''}`}>
                <div className="nav-container">
            <div className="nav-links">
              {/* Medical Alerts Dropdown */}
              <div 
                className="nav-dropdown"
                onMouseEnter={() => !mobileMenuOpen && setActiveDropdown('medical-alerts')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {mobileMenuOpen ? (
                  <button
                    type="button"
                    className="nav-link dropdown-toggle nav-link-button"
                    onClick={() => setMobileMedicalAlertsOpen(!mobileMedicalAlertsOpen)}
                  >
                    MEDICAL ALERTS
                  </button>
                ) : (
                  <Link 
                    to="/medical-alerts"
                    className="nav-link dropdown-toggle"
                  >
                    MEDICAL ALERTS
                  </Link>
                )}
                {/* Desktop: hover dropdown */}
                {activeDropdown === 'medical-alerts' && !mobileMenuOpen && (
                  <div className="dropdown-menu-large">
                    <div className="dropdown-products">
                      <Link 
                        to="/products/ez-care" 
                        className="dropdown-product-card"
                        onClick={handleDropdownClose}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div className="product-image-box">
                          {dropdownImage1 && <img src={dropdownImage1} alt="EZ Care In-Home" />}
                        </div>
                        <h4>EZ CARE IN-HOME MEDICAL ALERT</h4>
                      </Link>
                      <Link 
                        to="/products/ez-care-plus" 
                        className="dropdown-product-card"
                        onClick={handleDropdownClose}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div className="product-image-box">
                          {dropdownImage2 && <img src={dropdownImage2} alt="EZ Care Plus" />}
                        </div>
                        <h4>EZ CARE PLUS IN-HOME MEDICAL ALERT</h4>
                      </Link>
                      <Link 
                        to="/products/lifebeacon" 
                        className="dropdown-product-card"
                        onClick={handleDropdownClose}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div className="product-image-box">
                          {dropdownImage3 && <img src={dropdownImage3} alt="LifeBeacon" />}
                        </div>
                        <h4>LIFEBEACON ON-THE-GO PENDANT/WRISTBAND ALERT</h4>
                      </Link>
                    </div>
                    <div className="dropdown-buttons">
                      <Link to="/pricing#product-tiles" className="dropdown-btn" onClick={handleDropdownClose}>
                        COMPARE PRODUCTS
                      </Link>
                      <Link to="/accessories" className="dropdown-btn" onClick={handleDropdownClose}>
                        ACCESSORIES
                      </Link>
                      <Link to="/where-it-works" className="dropdown-btn" onClick={handleDropdownClose}>
                        WHERE IT WORKS
                      </Link>
                      <Link to="/how-it-works" className="dropdown-btn" onClick={handleDropdownClose}>
                        HOW IT WORKS
                      </Link>
                    </div>
                  </div>
                )}
                {/* Mobile: expandable submenu */}
                {mobileMenuOpen && mobileMedicalAlertsOpen && (
                  <div className="nav-mobile-submenu">
                    <Link to="/products/ez-care" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      EZ Care In-Home
                    </Link>
                    <Link to="/products/ez-care-plus" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      EZ Care Plus
                    </Link>
                    <Link to="/products/lifebeacon" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      LifeBeacon On-the-Go
                    </Link>
                    <Link to="/pricing#product-tiles" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      Compare Products
                    </Link>
                    <Link to="/accessories" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      Accessories
                    </Link>
                    <Link to="/where-it-works" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      Where It Works
                    </Link>
                    <Link to="/how-it-works" className="nav-mobile-sublink" onClick={handleDropdownClose}>
                      How It Works
                    </Link>
                  </div>
                )}
              </div>
              
              <Link to="/pricing" className="nav-link" onClick={handleDropdownClose}>PRICING</Link>
              <Link to="/healthcare-providers" className="nav-link" onClick={handleDropdownClose}>FOR HEALTHCARE PROVIDERS</Link>
              <Link to="/current-members" className="nav-link" onClick={handleDropdownClose}>FOR CURRENT MEMBERS</Link>
              {/* Admin link removed - access via direct URL only */}
            </div>
                </div>
              </div>
        </nav>
        </NavigationWrapper>
        
        <main className="main-content">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/medical-alerts" element={<MedicalAlertsPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/products/medical-alert-bracelets/796" element={<MedicalAlertBraceletsPage />} />
              <Route path="/products/:productId" element={<ProductDetailPage />} />
              <Route path="/our-story" element={<OurStoryPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/my-account" element={<MyAccountPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-success" element={<OrderSuccessPage />} />
              <Route path="/healthcare-providers" element={<HealthcareProvidersPage />} />
              <Route path="/current-members" element={<CurrentMembersPage />} />
              <Route path="/refer-friend" element={<ReferFriendPage />} />
              <Route path="/accessories/:slug" element={<AccessoryDetailPage />} />
              <Route path="/accessories" element={<AccessoriesPage />} />
              <Route path="/where-it-works" element={<WhereItWorksPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/blog" element={<BlogListingPage />} />
              <Route path="/blog/fall-prevention" element={<BlogFallPreventionPage />} />
              <Route path="/blog/home-safety" element={<BlogHomeSafetyPage />} />
              <Route path="/blog/caregiver-payment" element={<BlogCaregiverPaymentPage />} />
              {/* Legacy WordPress blog URLs: /blog/general/slug/5960 */}
              <Route path="/blog/:category/:slug/:postId" element={<BlogPostPage />} />
              {/* Dynamic blog post route */}
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* Utility Pages */}
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/why-alert1/first-alert-medical-system" element={<FirstAlertMedicalSystemPage />} />
              <Route path="/resources/medicare-medicaid-medical-alert" element={<MedicareMedicaidPage />} />
              <Route path="/resources/senior-cell-phone-reviews-medical-alerts/7125" element={<SeniorCellPhoneReviewsPage />} />
              <Route path="/resources/medical-alert-system-glossary/209" element={<MedicalAlertGlossaryPage />} />
          <Route path="/resources/baby-boomer-facts-and-statistics/426" element={<BabyBoomerFactsPage />} />
              <Route path="/pricing/compare-medical-alert-systems" element={<CompareMedicalAlertSystemsPage />} />
              <Route path="/pricing/compare-alert1-to-life-alert" element={<CompareAlert1ToLifeAlertPage />} />
              <Route path="/pricing/compare-alert1-to-bay-alarm" element={<CompareAlert1ToBayAlarmPage />} />
              <Route path="/pricing/compare-alert1-to-medical-guardian" element={<CompareAlert1ToMedicalGuardianPage />} />
              <Route path="/pricing/compare-alert1-to-greatcall" element={<CompareAlert1ToGreatCallPage />} />
              <Route path="/pricing/compare-alert1-to-mobilehelp" element={<CompareAlert1ToMobileHelpPage />} />
              <Route path="/pricing/compare-alert1-to-adt-health" element={<CompareAlert1ToAdtHealthPage />} />
              <Route path="/content/personal-emergency-response-system/789" element={<PersonalEmergencyResponseSystemPage />} />
              <Route path="/life-alert-compared-to-alert1" element={<LifeAlertComparedToAlert1Page />} />
              <Route path="/why-alert1/life-alert-alternatives/791" element={<LifeAlertAlternativesPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/request-brochure" element={<RequestBrochurePage />} />
              <Route path="/request-admin-access" element={<RequestAdminAccess />} />
              <Route path="/decrypt-orders-report" element={<DecryptOrdersReportPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/troubleshooting" element={<TroubleshootingPage />} />
              {/* Legacy routes for development */}
              <Route path="/blog-admin" element={<BlogAdminPage />} />
              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/template-designer" element={<TemplateDesignerPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/messaging" element={<MessagingPage />} />
              <Route path="/shipments" element={<ShipmentsPage />} />
              <Route path="/excel-tools" element={<ExcelToolsPageMinimal />} />
              <Route path="/record-matching" element={<RecordMatchingPage />} />
              <Route path="/railway-health-check" element={<HealthCheckPage />} />
              <Route path="/agingparents" element={<LandingPageTest />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </main>
      </div>
    </>
  );
}

// Main App component with Image Provider
function App() {
  return (
    <ImageProvider>
      <ProductsProvider>
        <AppContent />
      </ProductsProvider>
    </ImageProvider>
  );
}

export default App;
